import moment from 'moment/moment'
import { Fragment, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { currencyFormat } from '../../utils/numbers'
import { e164ToPhone } from '../../utils/phone'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { BaseRecordView } from '../common/sections'
import { SubaccountsSection } from '../common/sections/SubaccountsSection'
import { CustomFieldView } from '../items/CustomFieldView'
import { ContactAvatar } from './ContactAvatar'

const viewWillAppear = async ({ urlParams, owner }: PocketComponent) => {
    const clientID = urlParams.id
    if (!clientID) {
        return new RecordNotFoundError('Client not found')
    }

    const customFields = owner.id
        ? await RecordFactory.getInstance().loadClientFields(owner.id, false)
        : []

    await RecordFactory.getInstance().fetchClient(clientID, false)
    const client = await RecordFactory.getInstance().loadClient(clientID)
    if (!client) {
        return new RecordNotFoundError('Client not found')
    }
    const user = client.person
    if (!user) {
        return new RecordNotFoundError('User not found')
    }

    const result = await RecordFactory.getInstance().fetchUser(user)
    if (!result.ok) {
        return new RecordNotFoundError('User not found')
    }

    const dashboardData = result.value

    const sources = user?.id ? await Source.validClientSources(user.id) : []

    return {
        client,
        user,
        customFields,
        sources,
        dashboardData,
        owner,
    }
}

const render = ({
    data,
    base: { me },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { client, user, customFields, sources, dashboardData, owner } = data

    usePageTitle(user.name ?? 'Client')

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const navigate = useNavigate()

    const backHistoryEntry = findLastHistoryEntry({ excluding: /^\/clients\/.*/ })

    const shouldHideContactDetails = client.id !== undefined && me.role === 'contractor'
    const shouldEnforcePrivacy = user.employer?.id !== undefined && owner.id !== user.employer?.id

    const otherDetailItems = (() => {
        const items: { id: string; title: string; content: ReactNode }[] = []

        if (client.channel) {
            items.push({
                id: 'lead-source',
                title: 'Lead source',
                content: (
                    <>
                        {client.channel.name} {client.cost && `(${currencyFormat(client.cost)})`}
                    </>
                ),
            })
        }
        else if (client.referer) {
            items.push({
                id: 'lead-source',
                title: 'Lead source',
                content: `Referred by ${client.referer.name}`,
            })
        }

        if (client.discount) {
            items.push({
                id: 'discount',
                title: 'Discount',
                content: `${client.discount}%`,
            })
        }

        items.push({
            id: 'smart-campaigns',
            title: 'Send smart campaigns',
            content: client.allowTrigger ? 'Yes' : 'No',
        })

        if (sources.length > 0) {
            items.push({
                id: 'sources',
                title: 'Payment methods',
                content: (
                    <div className="flex flex-col items-stretch justify-start gap-y-2">
                        {sources.map((source) => (
                            <div
                                key={source.id ?? ''}
                                className="flex items-center justify-start gap-x-2"
                            >
                                {source.icon} {source.displayTitle}
                            </div>
                        ))}
                    </div>
                ),
            })
        }

        for (const field of customFields) {
            const value = client.fieldValues[field.name ?? '']
            if (value === undefined) {
                continue
            }

            items.push({
                id: field.id ?? '',
                title: field.label ?? '',
                content: <CustomFieldView field={field} value={value} />,
            })
        }

        return items
    })()

    return (
        <BaseRecordView
            title="Client"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Clients',
                          onClick: () => navigate('/clients'),
                      }
            }
            headerButtons={[
                { title: 'Edit', onClick: () => navigate(`/clients/${client.id}/edit`) },
                { title: 'View history', onClick: () => navigate(`/dashboard/user/${user.id}`) },
            ]}
        >
            <BaseRecordView.Container>
                <BaseRecordView.LeftColumn>
                    <Card size="sm">
                        <div className="flex flex-col items-center justify-center">
                            <ContactAvatar user={user} className="size-[116px] text-[52px]" />

                            <Heading size="sm" className="mt-6 mb-1 text-center">
                                {user.name}
                            </Heading>

                            <div className="flex flex-col items-center justify-center gap-y-1">
                                {!!user.phone &&
                                    !shouldHideContactDetails &&
                                    (!shouldEnforcePrivacy || user.sms || user.employer?.sms) && (
                                        <Paragraph size="lg" className="font-normal text-gray-600">
                                            {e164ToPhone(
                                                (me.type === 'client' || shouldEnforcePrivacy) &&
                                                    user.sms
                                                    ? user.sms
                                                    : shouldEnforcePrivacy && !user.sms
                                                      ? user.employer?.sms
                                                      : user.e164
                                            )}
                                        </Paragraph>
                                    )}

                                {!!user.email &&
                                    !shouldHideContactDetails &&
                                    !shouldEnforcePrivacy && (
                                        <Paragraph size="lg" className="font-normal text-gray-600">
                                            <a
                                                href={`mailto:${user.email}`}
                                                className="text-primary-700 font-bold underline"
                                            >
                                                {user.email}
                                            </a>
                                        </Paragraph>
                                    )}
                            </div>

                            <div className="mt-6 flex items-center justify-center">
                                <a href={`/messages/${user.id}`}>
                                    <Button intent="secondary" size="medium">
                                        Chat
                                    </Button>
                                </a>
                            </div>
                        </div>

                        <Divider className="my-6" />

                        <div className="flex flex-col items-stretch justify-start gap-y-6">
                            <BaseRecordView.IconField
                                icon="dollar.large"
                                title={
                                    dashboardData.income !== undefined
                                        ? currencyFormat(dashboardData.income)
                                        : '-'
                                }
                                subtitle="Income"
                            />

                            <BaseRecordView.IconField
                                icon="calendar.large"
                                title={
                                    dashboardData.last_date
                                        ? moment(dashboardData.last_date).format('MMM Do, YYYY')
                                        : dashboardData.next_date
                                          ? moment(dashboardData.next_date).format('MMM Do, YYYY')
                                          : '-'
                                }
                                subtitle={
                                    dashboardData.last_date
                                        ? 'Last appointment'
                                        : 'Next appointment'
                                }
                            />

                            <BaseRecordView.IconField
                                icon="coins2.large"
                                title={
                                    dashboardData.balance !== undefined
                                        ? currencyFormat(dashboardData.balance)
                                        : '-'
                                }
                                subtitle="Balance"
                            />

                            {client.employee !== undefined && (
                                <BaseRecordView.IconField
                                    icon={<ContactAvatar user={client.employee} />}
                                    title={client.employee.name ?? ''}
                                    subtitle="Pro"
                                />
                            )}
                        </div>
                    </Card>

                    {owner.allowSubaccount && owner.industry?.subaccountType !== undefined && (
                        <SubaccountsSection
                            user={user}
                            industrySubaccountType={owner.industry.subaccountType}
                            owner={owner}
                        />
                    )}

                    {user.location !== undefined &&
                        (user.location ?? '').length > 0 &&
                        user.latitude !== undefined &&
                        user.longitude !== undefined && (
                            <BaseRecordView.LocationSection
                                location={user.location}
                                latLong={{
                                    latitude: user.latitude,
                                    longitude: user.longitude,
                                }}
                            />
                        )}
                </BaseRecordView.LeftColumn>

                <BaseRecordView.RightColumn>
                    <BaseRecordView.NotesSection target={{ type: 'user', user }} />

                    {otherDetailItems.length > 0 && (
                        <Card title="Other details">
                            <div className="mt-8 flex flex-col items-stretch justify-start">
                                {otherDetailItems.map(
                                    ({ id, title, content }, otherDetailItemIndex) => (
                                        <Fragment key={id}>
                                            <div className="flex flex-col items-stretch justify-center gap-y-2">
                                                <Label size="lg">{title}</Label>
                                                <Paragraph size="md" className="text-gray-600">
                                                    {content}
                                                </Paragraph>
                                            </div>

                                            {otherDetailItemIndex < otherDetailItems.length - 1 && (
                                                <Divider className="my-4" />
                                            )}
                                        </Fragment>
                                    )
                                )}
                            </div>
                        </Card>
                    )}
                </BaseRecordView.RightColumn>
            </BaseRecordView.Container>
        </BaseRecordView>
    )
}

export const ClientPage = usePSComponent(render, viewWillAppear)
